"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, FileText, BadgeCheck, CircleQuestionMark as HelpCircle, FileCheck2, Lock, Cookie, Accessibility } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface DisclosureItem {
  id: string;
  title: string;
  iconName: string;
  summary: string;
  content: string;
  keyRule: string;
}
const policyData: DisclosureItem[] = [{
  id: "advertising-policy",
  title: "Advertising policy",
  iconName: "ShieldCheck",
  summary: "Clear separation between commercial partnerships and clinical reviews.",
  content: "PetsWhirl maintains strict boundaries between advertising and our editorial operations. Advertisers never influence article conclusions, product ratings, or veterinary guidance. Sponsored placements and partner features are always explicitly labeled with prominent disclosure tags.",
  keyRule: "Sponsors have zero editorial veto power over medical assessments."
}, {
  id: "affiliate-disclosure",
  title: "Affiliate disclosure",
  iconName: "FileCheck2",
  summary: "Transparent commissions on editorially selected products.",
  content: "When you buy pet supplies or gear through links on our site, we may earn an affiliate commission from retailer partners. Our testing team purchases review samples independently or tests according to strict evaluation criteria. We only recommend gear that meets our safety and durability benchmarks.",
  keyRule: "Commission rates never influence product rankings or review outcomes."
}, {
  id: "corrections-policy",
  title: "Corrections and update protocol",
  iconName: "BadgeCheck",
  summary: "Public tracking of clinical revisions and factual updates.",
  content: "When veterinary research evolves or factual inaccuracies are identified, we promptly revise our content. Every updated clinical guide includes a timestamp and a transparent log noting what was modified. Readers can submit correction requests directly to our editorial review board.",
  keyRule: "All material factual corrections are recorded openly in the article header."
}, {
  id: "privacy-policy",
  title: "Privacy policy",
  iconName: "Lock",
  summary: "How we collect, protect, and respect your reader information.",
  content: "We collect minimal personal information necessary to deliver our pet care newsletters and respond to inquiries. We do not sell your personal data to third-party brokers. All submitted information is encrypted in transit and stored on secure cloud infrastructure.",
  keyRule: "Your personal data is never sold or shared with commercial marketers."
}, {
  id: "terms-of-service",
  title: "Terms of service and medical disclaimer",
  iconName: "FileText",
  summary: "Informational pet wellness guidance rather than individual clinical diagnosis.",
  content: "PetsWhirl provides evidence-informed pet care information for educational purposes. Our content does not replace direct diagnosis, treatment, or individualized medical plans from a licensed veterinarian. If your pet experiences an acute medical emergency, contact an animal hospital immediately.",
  keyRule: "Always consult a licensed veterinarian for urgent diagnosis and direct prescriptions."
}, {
  id: "cookie-policy",
  title: "Cookie policy and tracking controls",
  iconName: "Cookie",
  summary: "Transparent usage of functional, performance, and preference cookies.",
  content: "We use essential cookies to maintain site stability, along with anonymized analytics to measure which pet health topics help our readers most. You can adjust your browser settings or privacy preferences at any time to reject non-essential tracking.",
  keyRule: "You retain full control to manage or disable optional tracking cookies."
}, {
  id: "accessibility-statement",
  title: "Accessibility statement",
  iconName: "Accessibility",
  summary: "Commitment to accessible, legible digital pet health guidance for all readers.",
  content: "We adhere to Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. We regularly audit contrast ratios, semantic markup, keyboard navigability, and screen reader compatibility across all device sizes to ensure equal access.",
  keyRule: "Continuous auditing ensures full keyboard navigation and screen reader support."
}];
export default function Disclosures() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-foreground shrink-0" />;
      case "FileCheck2":
        return <FileCheck2 className="size-5 text-foreground shrink-0" />;
      case "BadgeCheck":
        return <BadgeCheck className="size-5 text-foreground shrink-0" />;
      case "Lock":
        return <Lock className="size-5 text-foreground shrink-0" />;
      case "FileText":
        return <FileText className="size-5 text-foreground shrink-0" />;
      case "Cookie":
        return <Cookie className="size-5 text-foreground shrink-0" />;
      case "Accessibility":
        return <Accessibility className="size-5 text-foreground shrink-0" />;
      default:
        return <FileText className="size-5 text-foreground shrink-0" />;
    }
  };
  return <section id="policies-disclosures" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }}>
            <span className="inline-block text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">
              Governance & Integrity
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-4">
              Publication standards and disclosures
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
              Clear guidelines on how we fund our research, verify medical claims, and handle your data with complete transparency.
            </p>
          </motion.div>
        </div>

        {/* Clinical Assurance Box */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.08,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-10 rounded-xl bg-card border border-border p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xs">
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-muted p-2.5 text-foreground shrink-0">
              <ShieldCheck className="size-6 text-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-serif font-semibold text-card-foreground mb-1">
                Editorial independence guarantee
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Veterinarians, certified animal behaviorists, and experienced pet care journalists review all advisory articles before publication.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 w-full md:w-auto">
            <Button asChild variant="outline" className="w-full md:w-auto border-border text-foreground hover:bg-muted font-sans font-medium text-sm">
              <Link to="/AboutPetsWhirlPage">
                Read our medical charter
              </Link>
            </Button>
          </div>
        </motion.div>

        {/* Accordion Component */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.15,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-xl border border-border bg-background p-2 sm:p-6 shadow-xs">
          <Accordion type="single" collapsible defaultValue="advertising-policy" className="w-full">
            {policyData.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-b border-border last:border-b-0 py-1">
                <AccordionTrigger className="hover:no-underline py-4 px-3 sm:px-4 rounded-lg hover:bg-card text-left transition-colors">
                  <div className="flex items-center gap-3 sm:gap-4 pr-2">
                    <div className="p-2 rounded-md bg-muted text-foreground shrink-0">
                      {getIcon(item.iconName)}
                    </div>
                    <div>
                      <span className="font-serif font-semibold text-base sm:text-lg text-foreground block">
                        {item.title}
                      </span>
                      <span className="text-xs sm:text-sm text-muted-foreground font-sans font-normal line-clamp-1 mt-0.5">
                        {item.summary}
                      </span>
                    </div>
                  </div>
                </AccordionTrigger>
                
                <AccordionContent className="px-3 sm:px-4 pt-2 pb-6">
                  <div className="space-y-4 pl-0 sm:pl-11">
                    <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                      {item.content}
                    </p>
                    
                    {/* Assurance Callout Line */}
                    <div className="rounded-lg bg-card border-l-2 border-primary p-4 border border-border">
                      <div className="flex items-start gap-2.5">
                        <span className="text-xs font-semibold uppercase tracking-wider text-foreground block shrink-0 mt-0.5">
                          Standard:
                        </span>
                        <p className="text-xs sm:text-sm text-foreground font-medium">
                          {item.keyRule}
                        </p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {/* Bottom Support & Questions Card */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.22,
        ease: [0.16, 1, 0.3, 1]
      }} className="mt-12 rounded-xl bg-card border border-border p-6 sm:p-8 text-center max-w-2xl mx-auto">
          <div className="inline-flex p-3 rounded-full bg-muted text-foreground mb-4">
            <HelpCircle className="size-6" />
          </div>
          <h3 className="font-serif text-xl font-semibold text-card-foreground mb-2">
            Have questions about our standards?
          </h3>
          <p className="text-sm text-muted-foreground font-sans leading-relaxed mb-6">
            If you notice an outdated clinical source or need further clarification on our product reviews, our editorial desk is here to help.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <a href="mailto:editorial@petswhirl.org">
                Contact editorial desk
              </a>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto border-border text-foreground hover:bg-muted">
              <Link to="/PetHealthPage">
                Browse reviewed topics
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}